import React, { useRef, useState, useEffect } from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import ExtensionIcon from '@mui/icons-material/Extension';
import TollIcon from '@mui/icons-material/Toll';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import type { SxProps } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import SEO from './SEO';
import { trackEvent } from './analytics';

function useScrollFade(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const fadeStyle = (inView: boolean, delay = 0): SxProps => ({
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0)' : 'translateY(28px)',
  transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
});

export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const howItWorksRef = useRef<HTMLDivElement>(null);
  const scrollToHowItWorks = () => howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });

  const audienceSection = useScrollFade();
  const statsSection = useScrollFade();
  const stepsSection = useScrollFade();
  const benefitsSection = useScrollFade();
  const operatorSection = useScrollFade();
  const ctaSection = useScrollFade();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO
        title="App Integrations & Partnerships — Embed AI Safety in Your Platform"
        description="SMCLA's AI child safety detection can be embedded directly into existing daycare management platforms. Partner with us to give your users real-time safety monitoring without changing their workflow."
        path="/integrations"
      />
      <Header />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 16 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.08) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <ExtensionIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Partnership Program
            </Typography>
          </Box>

          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '4rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2, lineHeight: 1.05 }}>
            Your daycare app,<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>now with AI safety built in.</Box>
          </Typography>

          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 620, mx: 'auto', lineHeight: 1.65, mb: 6 }}>
            SMCLA's real-time child safety detection can be embedded directly into the daycare management platforms operators already use every day — no new tool, no extra login, no switching cost.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => { trackEvent('cta_click', 'Become a Partner', pathname); navigate('/contact'); }}
              sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
            >
              Become a Partner →
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={scrollToHowItWorks}
              sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 15, color: '#9499b0', borderColor: 'rgba(148,153,176,0.3)', borderRadius: 2, '&:hover': { borderColor: '#9499b0', bgcolor: 'rgba(255,255,255,0.03)' } }}
            >
              See how it works ↓
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Stat strip ── */}
      <Box ref={statsSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 5, md: 7 }, px: 3, ...fadeStyle(statsSection.inView) }}>
        <Container maxWidth="md">
          <Grid container spacing={0}>
            {[
              { stat: '85%', label: 'of daycare operators use a management app daily' },
              { stat: '1 platform', label: 'they check — make it the one with safety built in' },
              { stat: 'Zero', label: 'switching cost for operators already on your platform' },
            ].map(({ stat, label }, i) => (
              <Grid key={stat} size={{ xs: 12, md: 4 }}>
                <Box sx={{
                  textAlign: 'center',
                  py: { xs: 3, md: 4 },
                  px: 3,
                  borderRight: { md: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none' },
                  borderBottom: { xs: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none', md: 'none' },
                }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2rem', md: '2.5rem' }, color: '#5ce1e6', lineHeight: 1, mb: 1 }}>
                    {stat}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0' }}>
                    {label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Two audiences ── */}
      <Box ref={audienceSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(audienceSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Who This Is For
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1.5 }}>
            Built for platforms. Delivered to operators.
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            The partnership model serves two groups — the companies that build daycare software, and the operators who run on it.
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderTop: '3px solid #5ce1e6', borderRadius: 3, height: '100%' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
                  For App Partners — Software Companies
                </Typography>
                <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.7rem' }, color: '#f0f0f8', mb: 2, lineHeight: 1.25 }}>
                  Add the feature no other daycare platform has.
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.75, mb: 3 }}>
                  AI-powered child safety detection, natively embedded in your product. Differentiate in a crowded market, unlock enterprise accounts that require safety compliance, and generate a new revenue stream — without building the AI yourself.
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    'Revenue share on every SMCLA seat activated through your platform',
                    'Enterprise accounts demand safety compliance — now you can close them',
                    'No AI development cost — SMCLA handles detection, you handle UX',
                    'Co-marketing, joint sales support, and technical integration team',
                  ].map((item) => (
                    <Box key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#5ce1e6', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.15)', borderTop: '3px solid #ff9f43', borderRadius: 3, height: '100%' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff9f43', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 2 }}>
                  For Daycare Operators — Already on a Platform
                </Typography>
                <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.4rem', md: '1.7rem' }, color: '#f0f0f8', mb: 2, lineHeight: 1.25 }}>
                  Safety monitoring in the app you already open 20× a day.
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.75, mb: 3 }}>
                  Already on Brightwheel, HiMama, Procare, or another platform? If your provider is a SMCLA partner, alerts surface directly inside your existing dashboard. No new app, no new login — safety is just there.
                </Typography>
                <Stack spacing={1.5}>
                  {[
                    'Real-time alerts appear natively in the platform you already use',
                    'No workflow disruption — staff don\'t need to learn a new tool',
                    'Investigation clips accessible from your current system',
                    'Ask your platform provider about SMCLA integration',
                  ].map((item) => (
                    <Box key={item} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#ff9f43', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── How integration works ── */}
      <Box ref={howItWorksRef}>
        <Box ref={stepsSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(stepsSection.inView) }}>
          <Container maxWidth="md">
            <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
              The Integration
            </Typography>
            <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
              Plug in. Brand it. Ship it.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
              SMCLA is built to integrate cleanly into existing platforms. Technical lift is minimal — most partners go from contract to live integration in under 6 weeks.
            </Typography>

            <Stack spacing={2.5}>
              {[
                {
                  num: '1',
                  title: 'API or SDK connection',
                  body: 'SMCLA connects to your platform via REST API or embeddable SDK. Camera feeds are routed through our detection infrastructure — no change to your existing camera or storage setup required.',
                },
                {
                  num: '2',
                  title: 'Alert routing into your dashboard',
                  body: 'When SMCLA detects a flagged event, the alert surfaces natively inside your product\'s notification layer and dashboard. Your UX, your design — SMCLA powers what\'s behind it.',
                },
                {
                  num: '3',
                  title: 'White-label or co-branded — your choice',
                  body: 'Run it as "Safety Monitoring by [Your Platform]" with SMCLA invisible to your users, or co-brand it. Either way, SMCLA handles detection, model updates, and infrastructure.',
                },
                {
                  num: '4',
                  title: 'Joint onboarding & ongoing support',
                  body: 'SMCLA handles facility-level setup and camera configuration for each new customer. You handle billing and account management. Dedicated integration engineers stay available through launch and beyond.',
                },
              ].map(({ num, title, body }) => (
                <Box key={num} sx={{ display: 'flex', gap: 3, p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, alignItems: 'flex-start', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.25)' } }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#5ce1e6' }}>{num}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Container>
        </Box>
      </Box>

      {/* ── Partner benefits grid ── */}
      <Box ref={benefitsSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(benefitsSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Partner Benefits
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1.5 }}>
            What you get as a partner.
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 7, maxWidth: 480, mx: 'auto' }}>
            A revenue share, a technical integration team, and a feature that no competitor in your market has shipped.
          </Typography>

          <Grid container spacing={2.5}>
            {[
              {
                icon: <TollIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'New revenue stream',
                body: 'Earn a revenue share on every SMCLA seat activated through your platform — automatically, with no additional sales motion required from your team.',
              },
              {
                icon: <RocketLaunchIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Competitive moat',
                body: 'No other daycare management platform ships AI behavioral safety detection as a native feature. First mover advantage is still fully available — and meaningful in this market.',
              },
              {
                icon: <LockOpenIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Enterprise account unlock',
                body: 'Multi-site operators and franchise chains increasingly require safety monitoring as a baseline. SMCLA integration lets you close accounts that were previously out of reach.',
              },
              {
                icon: <SupportAgentIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Full technical support',
                body: 'Dedicated integration engineers, SLA-backed API uptime, co-marketing support, and a shared pipeline for joint sales opportunities with large daycare groups.',
              },
            ].map(({ icon, title, body }, i) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, height: '100%', transition: 'all 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.25)', transform: 'translateY(-3px)', boxShadow: '0 16px 32px rgba(92,225,230,0.06)' }, ...fadeStyle(benefitsSection.inView, i * 0.08) }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'inline-flex', p: 1, bgcolor: 'rgba(92,225,230,0.08)', borderRadius: 1.5 }}>{icon}</Box>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8' }}>{title}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── What operators get ── */}
      <Box ref={operatorSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(operatorSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            For Operators on Partner Platforms
          </Typography>
          <Typography variant="h2" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 1.5 }}>
            Safety monitoring without changing anything.
          </Typography>
          <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 7, maxWidth: 500, mx: 'auto', lineHeight: 1.65 }}>
            If your current platform integrates SMCLA, you get all of this without touching your existing setup.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                accent: '#5ce1e6',
                icon: <NotificationsActiveIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
                title: 'Alerts in the app you already use',
                body: 'Safety flags arrive inside your existing platform dashboard and notifications — the same screen you check for attendance, billing, and messaging. No new tab to open.',
              },
              {
                accent: '#ff9f43',
                icon: <SwapHorizIcon sx={{ color: '#ff9f43', fontSize: 22 }} />,
                title: 'Zero workflow disruption',
                body: 'Staff don\'t need to learn new software. Alerts surface where they already work. No new login, no app switching, no change management overhead.',
              },
              {
                accent: '#8c52ff',
                icon: <FolderCopyIcon sx={{ color: '#8c52ff', fontSize: 22 }} />,
                title: 'Investigation records in your current system',
                body: 'Flagged clips, chain-of-custody metadata, and investigation notes are stored and accessible directly from your platform — ready for review, legal proceedings, or regulatory filings.',
              },
            ].map(({ accent, icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: `1px solid ${accent}20`, borderTop: `3px solid ${accent}`, borderRadius: 3, height: '100%' }}>
                  <Box sx={{ display: 'inline-flex', p: 1, bgcolor: `${accent}12`, borderRadius: 1.5, mb: 2 }}>{icon}</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 1.5 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ── */}
      <Box ref={ctaSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, textAlign: 'center', ...fadeStyle(ctaSection.inView) }}>
        <Container maxWidth="sm">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 2 }}>
            Partnership Program
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Ready to integrate?
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', lineHeight: 1.65, mb: 5 }}>
            We work with a small number of integration partners per quarter. If you run a daycare management platform and want to be first in your market with AI safety built in, reach out now.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => { trackEvent('cta_click', 'Partner with SMCLA', pathname); navigate('/contact'); }}
            sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            Partner with SMCLA →
          </Button>
          <Typography sx={{ mt: 2, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.5)' }}>
            We respond to all partnership inquiries within one business day.
          </Typography>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
